import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { getAuthHeaders } from '../utils/api';
import StravaIntegration from './StravaIntegration';
import HeadshotUploader from './HeadshotUploader';
import AthleteHeadshot from './AthleteHeadshot';
import { parseTwoKTime, formatTwoKTime } from '../utils/twoKTime';
import {
  TwoKGoalsEditor,
  TwoKGoalsSummary,
  emptyClassGoalState,
  classGoalsToPayload
} from './TwoKGoalsEditor';
import KorzoSelfProg from './KorzoSelfProg';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim());

export default function UserProfile() {
  const { user, token, logout, leaveClub } = useAuth();
  const { teamSettings } = useTheme();
  const targetTimesEnabled = Boolean(teamSettings?.targetTimesEnabled);

  // Profile info state
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [team, setTeam] = useState(user?.team || '');

  // Athlete profile state
  const [athleteProfile, setAthleteProfile] = useState(null);
  const [isEditingAthlete, setIsEditingAthlete] = useState(false);
  const [athleteStatus, setAthleteStatus] = useState('');
  const [athleteFormData, setAthleteFormData] = useState({
    racingclass: '',
    phone: '',
    height: '',
    weight: '',
    ergScore: '',
    goal2kTime: '',
    coachGoal2kTime: ''
  });
  const [classGoalState, setClassGoalState] = useState(emptyClassGoalState());

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Email preferences state
  const [emailPreferences, setEmailPreferences] = useState({
    workoutNotifications: true,
    absenceConfirmations: true,
    teamAnnouncements: true
  });

  // UI state
  const [profileStatus, setProfileStatus] = useState('');
  const [passwordStatus, setPasswordStatus] = useState('');
  const [preferencesStatus, setPreferencesStatus] = useState('');
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [leavingClub, setLeavingClub] = useState(false);

  const clubMemberships = user?.clubMemberships?.length
    ? user.clubMemberships
    : (user?.clubs || []).map((clubCode) => ({ clubCode, displayName: clubCode }));

  useEffect(() => {
    fetchEmailPreferences();
    if (user?.athleteId) {
      fetchAthleteProfile();
    }
  }, [user?.athleteId]);

  const fetchAthleteProfile = async () => {
    if (!user?.athleteId) return;
    
    try {
      const response = await fetch(`${API_URL}/api/athletes/${user.athleteId}`, {
        headers: getAuthHeaders(token)
      });

      if (response.ok) {
        const data = await response.json();
        setAthleteProfile(data);
        // Normalize erg_score to ergScore for form
        setAthleteFormData({
          racingclass: data.racingclass || '',
          phone: data.phone || '',
          height: data.height || '',
          weight: data.weight || '',
          ergScore: data.erg_score || data.ergScore || '',
          goal2kTime: formatTwoKTime(data.goal_2k_seconds),
          coachGoal2kTime: formatTwoKTime(data.coach_goal_2k_seconds)
        });
        setClassGoalState(emptyClassGoalState(data.two_k_goals));
      } else {
        console.error('Failed to fetch athlete profile');
      }
    } catch (error) {
      console.error('Error fetching athlete profile:', error);
    }
  };

  const handleAthleteUpdate = async (e) => {
    e.preventDefault();
    if (!user?.athleteId) {
      setAthleteStatus('Error: No athlete profile linked to your account');
      return;
    }
    
    setAthleteStatus('Updating...');

    try {
      const requestBody = {
        name: athleteProfile?.name || user.name,
        racingclass: athleteFormData.racingclass,
        // Account email is the login identity; avoid writing a stale roster email back.
        email: user.email,
        phone: athleteFormData.phone,
        height: athleteFormData.height,
        weight: athleteFormData.weight,
        ergScore: athleteFormData.ergScore,
        goal2kSeconds: parseTwoKTime(athleteFormData.goal2kTime),
        twoKGoals: classGoalsToPayload(classGoalState, false),
        team: athleteProfile?.team || user.team
      };

      console.log('[handleAthleteUpdate] Updating athlete:', user.athleteId, 'with data:', requestBody);

      const response = await fetch(`${API_URL}/api/athletes/${user.athleteId}`, {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();
      console.log('[handleAthleteUpdate] Response status:', response.status, 'data:', data);

      if (response.ok) {
        setAthleteStatus('Athlete profile updated successfully!');
        setIsEditingAthlete(false);
        await fetchAthleteProfile();
      } else {
        const errorMsg = data.error || data.message || 'Failed to update athlete profile';
        console.error('[handleAthleteUpdate] Error:', errorMsg);
        setAthleteStatus(`Error: ${errorMsg}`);
      }
    } catch (error) {
      console.error('Error updating athlete profile:', error);
      setAthleteStatus(`Error: ${error.message || 'Failed to update athlete profile. Please try again.'}`);
    }
  };

  const handleCancelAthleteEdit = () => {
    if (athleteProfile) {
      setAthleteFormData({
        racingclass: athleteProfile.racingclass || '',
        phone: athleteProfile.phone || '',
        height: athleteProfile.height || '',
        weight: athleteProfile.weight || '',
        ergScore: athleteProfile.erg_score || athleteProfile.ergScore || '',
        goal2kTime: formatTwoKTime(athleteProfile.goal_2k_seconds),
        coachGoal2kTime: formatTwoKTime(athleteProfile.coach_goal_2k_seconds)
      });
      setClassGoalState(emptyClassGoalState(athleteProfile.two_k_goals));
    }
    setIsEditingAthlete(false);
    setAthleteStatus('');
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    const trimmedEmail = email.trim();

    if (!isValidEmail(trimmedEmail)) {
      setProfileStatus('Error: Please enter a valid email address');
      return;
    }

    setProfileStatus('Updating...');

    try {
      const response = await fetch(`${API_URL}/api/auth/update-profile`, {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify({ name: name.trim(), email: trimmedEmail, team: team || null }),
      });

      const data = await response.json();

      if (response.ok) {
        setProfileStatus('Profile updated successfully!');
        setIsEditingProfile(false);

        // If email was changed, user needs to log in again
        if (trimmedEmail !== user.email) {
          setTimeout(() => {
            alert('Email updated. Please log in again with your new email.');
            logout();
          }, 1500);
        } else {
          // Just refresh the page to update the user context
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        }
      } else {
        setProfileStatus(`Error: ${data.error || 'Failed to update profile'}`);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setProfileStatus('Error updating profile. Please try again.');
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordStatus('');

    // Validation
    if (newPassword.length < 8) {
      setPasswordStatus('New password must be at least 8 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordStatus('New passwords do not match');
      return;
    }

    setPasswordStatus('Updating password...');

    try {
      const response = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify({
          currentPassword,
          newPassword,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setPasswordStatus('Password changed successfully!');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setIsChangingPassword(false);
        setTimeout(() => setPasswordStatus(''), 3000);
      } else {
        setPasswordStatus(`Error: ${data.error || 'Failed to change password'}`);
      }
    } catch (error) {
      console.error('Error changing password:', error);
      setPasswordStatus('Error changing password. Please try again.');
    }
  };

  const handleCancelProfileEdit = () => {
    setName(user?.name || '');
    setEmail(user?.email || '');
    setTeam(user?.team || '');
    setIsEditingProfile(false);
    setProfileStatus('');
  };

  const handleCancelPasswordChange = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setIsChangingPassword(false);
    setPasswordStatus('');
  };

  const fetchEmailPreferences = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/email-preferences`, {
        headers: getAuthHeaders(token)
      });

      if (response.ok) {
        const data = await response.json();
        setEmailPreferences(data.preferences);
      }
    } catch (error) {
      console.error('Error fetching email preferences:', error);
    }
  };

  const handlePreferenceChange = async (preference, value) => {
    const updatedPreferences = { ...emailPreferences, [preference]: value };
    setEmailPreferences(updatedPreferences);
    setPreferencesStatus('Saving...');

    try {
      const response = await fetch(`${API_URL}/api/auth/email-preferences`, {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify({ [preference]: value })
      });

      if (response.ok) {
        setPreferencesStatus('Saved!');
        setTimeout(() => setPreferencesStatus(''), 2000);
      } else {
        setPreferencesStatus('Error saving preferences');
        setTimeout(() => setPreferencesStatus(''), 3000);
      }
    } catch (error) {
      console.error('Error updating email preferences:', error);
      setPreferencesStatus('Error saving preferences');
      setTimeout(() => setPreferencesStatus(''), 3000);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-theme-bg flex items-center justify-center">
        <p className="text-theme-primary">Loading...</p>
      </div>
    );
  }

  return (
    <main className="text-theme-primary p-2 sm:p-0">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">My Profile</h1>

        {/* Account Information Card */}
        <div className="bg-white p-4 sm:p-6 rounded-xl shadow-lg mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
            <h2 className="text-xl sm:text-2xl font-bold">Account Information</h2>
            {!isEditingProfile && (
              <button
                onClick={() => setIsEditingProfile(true)}
                className="btn-theme-primary px-4 py-3 sm:py-2 rounded w-full sm:w-auto"
              >
                Edit Profile
              </button>
            )}
          </div>

          {!isEditingProfile ? (
            // View Mode
            <div className="space-y-3 sm:space-y-4">
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-600 mb-1">
                  Name
                </label>
                <p className="text-base sm:text-lg">{user.name}</p>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-600 mb-1">
                  Email
                </label>
                <p className="text-base sm:text-lg break-all">{user.email}</p>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-600 mb-1">
                  Club
                </label>
                {clubMemberships.length > 1 ? (
                  <div className="mt-1 space-y-2">
                    {clubMemberships.map((membership) => (
                      <div key={membership.clubCode} className="flex items-center justify-between gap-2 text-sm">
                        <span className={membership.clubCode === user.clubCode ? 'font-semibold' : 'text-gray-600'}>
                          {membership.displayName || membership.clubCode}
                          {membership.clubCode === user.clubCode ? ' (active)' : ''}
                        </span>
                        <button
                          type="button"
                          disabled={leavingClub}
                          onClick={async () => {
                            if (!window.confirm(`Leave ${membership.displayName || membership.clubCode}? You can rejoin later with an invitation.`)) {
                              return;
                            }
                            setLeavingClub(true);
                            const result = await leaveClub(membership.clubCode);
                            setLeavingClub(false);
                            if (!result.success) {
                              alert(result.error || 'Failed to leave club');
                            } else {
                              window.location.reload();
                            }
                          }}
                          className="text-red-600 hover:text-red-800 text-xs font-medium disabled:opacity-50"
                        >
                          Leave
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-base sm:text-lg">{user.clubCode}</p>
                )}
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-600 mb-1">
                  Team
                </label>
                <p className="text-base sm:text-lg">{user.team || <span className="text-gray-400 italic">Not set</span>}</p>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-600 mb-1">
                  Role
                </label>
                <p className="text-base sm:text-lg capitalize">{user.role}</p>
              </div>

              {user.athleteId && athleteProfile && (
                <div className="flex items-center gap-3">
                  <AthleteHeadshot name={user.name} headshotUrl={athleteProfile.headshot_url} size="md" />
                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-gray-600 mb-1">
                      Athlete Profile
                    </label>
                    <p className="text-base sm:text-lg">
                      {athleteProfile.racingclass ? `Racing Class: ${athleteProfile.racingclass}` : 'No racing class set'}
                      {athleteProfile.weight && ` • Weight: ${athleteProfile.weight}`}
                      {athleteProfile.height && ` • Height: ${athleteProfile.height}`}
                    </p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            // Edit Mode
            <form onSubmit={handleProfileUpdate} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="border p-3 rounded w-full bg-white text-blue-900 text-base"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="border p-3 rounded w-full bg-white text-theme-primary text-base"
                />
                <p className="text-xs text-gray-500 mt-1">
                  This is your login email and will also update your linked athlete profile.
                  You will need to sign in again with the new address.
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-1">
                  Club
                </label>
                <p className="text-base sm:text-lg text-gray-500">{user.clubCode} (cannot be changed)</p>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-1">
                  Team <span className="font-normal text-gray-500">(optional)</span>
                </label>
                <input
                  type="text"
                  value={team}
                  onChange={(e) => setTeam(e.target.value)}
                  placeholder="e.g., HPG, Varsity, JV"
                  className="border p-3 rounded w-full bg-white text-theme-primary text-base"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Your team within the club (for filtering workouts, attendance, etc.)
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-1">
                  Role
                </label>
                <p className="text-base sm:text-lg capitalize text-gray-500">{user.role} (cannot be changed)</p>
              </div>

              {profileStatus && (
                <div className={`p-3 rounded text-sm ${profileStatus.startsWith('Error') ? 'bg-red-100 text-red-800' : profileStatus.includes('successfully') ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                  {profileStatus}
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  type="submit"
                  className="btn-theme-primary px-4 py-3 rounded text-base"
                >
                  Save Changes
                </button>
                <button
                  type="button"
                  onClick={handleCancelProfileEdit}
                  className="bg-gray-500 text-white px-4 py-3 rounded hover:bg-gray-600 text-base"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Athlete Profile Card - Only show if user has an athlete profile */}
        {user?.athleteId && athleteProfile && (
          <div className="bg-white p-4 sm:p-6 rounded-xl shadow-lg mb-4 sm:mb-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
              <h2 className="text-xl sm:text-2xl font-bold">Athlete Profile</h2>
              {!isEditingAthlete && (
                <button
                  onClick={() => setIsEditingAthlete(true)}
                  className="btn-theme-primary px-4 py-3 sm:py-2 rounded w-full sm:w-auto"
                >
                  Edit Athlete Profile
                </button>
              )}
            </div>

            {!isEditingAthlete ? (
              // View Mode
              <div className="space-y-3 sm:space-y-4">
                <HeadshotUploader
                  athlete={athleteProfile}
                  onUpdated={(updated) => setAthleteProfile((prev) => ({ ...prev, ...updated }))}
                />
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-gray-600 mb-1">
                    Racing Class
                  </label>
                  <p className="text-base sm:text-lg">{athleteProfile.racingclass || <span className="text-gray-400 italic">Not set</span>}</p>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-gray-600 mb-1">
                    Phone
                  </label>
                  <p className="text-base sm:text-lg">{athleteProfile.phone || <span className="text-gray-400 italic">Not set</span>}</p>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-gray-600 mb-1">
                    Height
                  </label>
                  <p className="text-base sm:text-lg">{athleteProfile.height || <span className="text-gray-400 italic">Not set</span>}</p>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-gray-600 mb-1">
                    Weight
                  </label>
                  <p className="text-base sm:text-lg">{athleteProfile.weight || <span className="text-gray-400 italic">Not set</span>}</p>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-gray-600 mb-1">
                    2k Erg Score
                  </label>
                  <p className="text-base sm:text-lg">{athleteProfile.erg_score || athleteProfile.ergScore || <span className="text-gray-400 italic">Not set</span>}</p>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs sm:text-sm font-semibold text-gray-600 mb-1">
                    2k Goals
                  </label>
                  <TwoKGoalsSummary
                    twoKGoals={athleteProfile.two_k_goals}
                    defaultSeconds={athleteProfile.goal_2k_seconds}
                    defaultCoachSeconds={athleteProfile.coach_goal_2k_seconds}
                  />
                </div>
                {targetTimesEnabled && (
                <div className="sm:col-span-2">
                  <KorzoSelfProg athlete={athleteProfile} />
                </div>
                )}
              </div>
            ) : (
              // Edit Mode
              <form onSubmit={handleAthleteUpdate} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold mb-1">
                    Racing Class
                  </label>
                  <input
                    type="text"
                    value={athleteFormData.racingclass}
                    onChange={(e) => setAthleteFormData({ ...athleteFormData, racingclass: e.target.value })}
                    placeholder="e.g., Lightweight, Open"
                    className="border p-3 rounded w-full bg-white text-theme-primary text-base"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={athleteFormData.phone}
                    onChange={(e) => setAthleteFormData({ ...athleteFormData, phone: e.target.value })}
                    placeholder="e.g., (555) 123-4567"
                    className="border p-3 rounded w-full bg-white text-theme-primary text-base"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-1">
                    Height
                  </label>
                  <input
                    type="text"
                    value={athleteFormData.height}
                    onChange={(e) => setAthleteFormData({ ...athleteFormData, height: e.target.value })}
                    placeholder={'e.g., 6\'2" or 188cm'}
                    className="border p-3 rounded w-full bg-white text-theme-primary text-base"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-1">
                    Weight
                  </label>
                  <input
                    type="text"
                    value={athleteFormData.weight}
                    onChange={(e) => setAthleteFormData({ ...athleteFormData, weight: e.target.value })}
                    placeholder="e.g., 180 lbs or 82 kg"
                    className="border p-3 rounded w-full bg-white text-theme-primary text-base"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-1">
                    2k Erg Score
                  </label>
                  <input
                    type="text"
                    value={athleteFormData.ergScore}
                    onChange={(e) => setAthleteFormData({ ...athleteFormData, ergScore: e.target.value })}
                    placeholder="e.g., 6:45.2"
                    className="border p-3 rounded w-full bg-white text-theme-primary text-base"
                  />
                </div>

                <TwoKGoalsEditor
                  classGoalState={classGoalState}
                  onChange={setClassGoalState}
                  canEditCoach={false}
                  defaultGoalTime={athleteFormData.goal2kTime}
                  defaultCoachTime={athleteFormData.coachGoal2kTime}
                  onDefaultGoalChange={(value) => setAthleteFormData({ ...athleteFormData, goal2kTime: value })}
                  onDefaultCoachChange={() => {}}
                />

                {athleteStatus && (
                  <div className={`p-3 rounded text-sm ${athleteStatus.startsWith('Error') ? 'bg-red-100 text-red-800' : athleteStatus.includes('successfully') ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                    {athleteStatus}
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    type="submit"
                    className="btn-theme-primary px-4 py-3 rounded text-base"
                  >
                    Save Changes
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelAthleteEdit}
                    className="bg-gray-500 text-white px-4 py-3 rounded hover:bg-gray-600 text-base"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {/* Change Password Card */}
        <div className="bg-white p-4 sm:p-6 rounded-xl shadow-lg mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
            <h2 className="text-xl sm:text-2xl font-bold">Change Password</h2>
            {!isChangingPassword && (
              <button
                onClick={() => setIsChangingPassword(true)}
                className="btn-theme-primary px-4 py-3 sm:py-2 rounded w-full sm:w-auto"
              >
                Change Password
              </button>
            )}
          </div>

          {!isChangingPassword ? (
            <p className="text-gray-600 text-sm sm:text-base">
              Click the button above to change your password
            </p>
          ) : (
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-1">
                  Current Password
                </label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  className="border p-3 rounded w-full bg-white text-blue-900 text-base"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-1">
                  New Password (minimum 8 characters)
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                  className="border p-3 rounded w-full bg-white text-blue-900 text-base"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-1">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={8}
                  className="border p-3 rounded w-full bg-white text-blue-900 text-base"
                />
              </div>

              {passwordStatus && (
                <div className={`p-3 rounded text-sm ${passwordStatus.startsWith('Error') || passwordStatus.includes('do not match') || passwordStatus.includes('must be') ? 'bg-red-100 text-red-800' : passwordStatus.includes('successfully') ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                  {passwordStatus}
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  type="submit"
                  className="btn-theme-primary px-4 py-3 rounded text-base"
                >
                  Update Password
                </button>
                <button
                  type="button"
                  onClick={handleCancelPasswordChange}
                  className="bg-gray-500 text-white px-4 py-3 rounded hover:bg-gray-600 text-base"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Strava */}
        <div className="bg-white p-4 sm:p-6 rounded-xl shadow-lg mb-4 sm:mb-6">
          <h2 className="text-xl sm:text-2xl font-bold mb-4">Strava sync</h2>
          <StravaIntegration />
        </div>

        {/* Email Preferences Card */}
        <div className="bg-white p-4 sm:p-6 rounded-xl shadow-lg mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-4">
            <h2 className="text-xl sm:text-2xl font-bold">Email Notifications</h2>
            {preferencesStatus && (
              <span className={`text-sm ${preferencesStatus.includes('Error') ? 'text-red-600' : 'text-green-600'}`}>
                {preferencesStatus}
              </span>
            )}
          </div>

          <p className="text-gray-600 mb-4 text-sm sm:text-base">Choose which emails you'd like to receive:</p>

          <div className="space-y-3 sm:space-y-4">
            <label className="flex items-center justify-between p-3 sm:p-4 border rounded hover:bg-gray-50 cursor-pointer gap-3">
              <div className="flex-1">
                <div className="font-semibold text-sm sm:text-base">Workout Notifications</div>
                <div className="text-xs sm:text-sm text-gray-600">Get notified when new workouts are posted</div>
              </div>
              <input
                type="checkbox"
                checked={emailPreferences.workoutNotifications}
                onChange={(e) => handlePreferenceChange('workoutNotifications', e.target.checked)}
                className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 flex-shrink-0"
              />
            </label>

            <label className="flex items-center justify-between p-3 sm:p-4 border rounded hover:bg-gray-50 cursor-pointer gap-3">
              <div className="flex-1">
                <div className="font-semibold text-sm sm:text-base">Absence Confirmations</div>
                <div className="text-xs sm:text-sm text-gray-600">Get notified when you're marked absent</div>
              </div>
              <input
                type="checkbox"
                checked={emailPreferences.absenceConfirmations}
                onChange={(e) => handlePreferenceChange('absenceConfirmations', e.target.checked)}
                className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 flex-shrink-0"
              />
            </label>

            <label className="flex items-center justify-between p-3 sm:p-4 border rounded hover:bg-gray-50 cursor-pointer gap-3">
              <div className="flex-1">
                <div className="font-semibold text-sm sm:text-base">Team Announcements</div>
                <div className="text-xs sm:text-sm text-gray-600">Receive team-wide announcements from coaches</div>
              </div>
              <input
                type="checkbox"
                checked={emailPreferences.teamAnnouncements}
                onChange={(e) => handlePreferenceChange('teamAnnouncements', e.target.checked)}
                className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 flex-shrink-0"
              />
            </label>
          </div>

          <p className="text-xs text-gray-500 mt-4">
            Note: Important account-related emails (like welcome emails) will always be sent regardless of these preferences.
          </p>
        </div>

        {/* Account Actions */}
        <div className="bg-white p-4 sm:p-6 rounded-xl shadow-lg">
          <h2 className="text-xl sm:text-2xl font-bold mb-4">Account Actions</h2>
          <button
            onClick={logout}
            className="bg-red-600 text-white px-6 py-3 rounded hover:bg-red-700 w-full sm:w-auto text-base"
          >
            Log Out
          </button>
        </div>
      </div>
    </main>
  );
}
